import type { Entity } from '@backstage/catalog-model';
import type { ErrorApi, FeatureFlagsApi } from '@backstage/frontend-plugin-api';
import type {
  EnhancedNavItemBlueprint,
  EntityFilterNavItem,
  GetEntitiesByFilterCallback,
  Icon,
  LegacyExtensionDataRefToValue,
} from '@ipen-portal/plugin-common-react';

import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  errorApiRef,
  featureFlagsApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { getDisplayName, notEmpty } from '@ipen-portal/plugin-common';
import {
  getEntityCatalogLink,
  useGetEntitiesByFilterCallback,
} from '@ipen-portal/plugin-common-react';
import { useAsync } from 'react-use';
import { tss } from 'tss-react/mui';

import type { NavItem } from '../../../types';

const useStyles = tss.create({
  icon: {
    // make black white
    filter: 'grayscale(100%)',
    height: 32,
    objectFit: 'contain',
    width: 32,
  },
});

type NavItemOrFilterOrBackstageNavItem = LegacyExtensionDataRefToValue<
  typeof EnhancedNavItemBlueprint.dataRefs.target
>['value'];

const isEntityFilterNavItem = (
  item: NavItemOrFilterOrBackstageNavItem,
): item is EntityFilterNavItem => 'filter' in item;

class NavResolver {
  constructor(
    private readonly featureFlagApi: FeatureFlagsApi,
    private readonly getEntitiesByFilter: GetEntitiesByFilterCallback,
    private readonly errorApi: ErrorApi,
    private readonly classes: ReturnType<typeof useStyles>['classes'],
  ) {}

  /**
   * Resolves a NavItemOrFilter to list of NavItems
   */
  resolveNavItemOrFilter = async (
    item: NavItemOrFilterOrBackstageNavItem & {
      featureFlag?: string;
    },
  ): Promise<NavItem[]> => {
    if (item.featureFlag && !this.featureFlagApi.isActive(item.featureFlag)) {
      return [];
    }

    if (!isEntityFilterNavItem(item)) {
      return [item];
    }

    try {
      const entities =
        (await this.getEntitiesByFilter({
          filter: item.filter,
          order: [{ field: 'metadata.title', order: 'asc' }],
        })) ?? [];
      return entities.map(entity => ({
        pageTheme: item.pageTheme,
        ...this.createEntityNavItem(entity, item.fallbackIcon),
      }));
    } catch (e) {
      this.errorApi.post({
        message: `Failed to resolve dynamic navigation item for filter ${JSON.stringify(
          item.filter,
        )}: ${e instanceof Error ? e.message : ''}`,
        name: 'failed-to-resolve-dynamic-nav-item',
        stack: e instanceof Error ? e.stack : undefined,
      });
      return [];
    }
  };

  private readonly createEntityNavItem = (
    entity: Entity,
    fallbackIcon: Icon,
  ): NavItem => {
    const logo = entity.spec?.logo as string | undefined;
    const icon = logo
      ? (
          <img alt="logo" className={this.classes.icon} src={logo} />
        )
      : (
          fallbackIcon
        );
    const ref = stringifyEntityRef(entity);
    return {
      breadcrumbMatcher: breadcrumbs =>
        breadcrumbs.some(breadcrumb => breadcrumb.entityRef === ref),
      icon,
      target: getEntityCatalogLink(entity),
      title: getDisplayName(entity),
    };
  };
}

export const useResolveNavigation = (
  navGroups: readonly (NavItemOrFilterOrBackstageNavItem & {
    featureFlag?: string;
  })[],
) => {
  const { classes } = useStyles();
  const featureFlagApi = useApi(featureFlagsApiRef);
  const getEntitiesByFilter = useGetEntitiesByFilterCallback();
  const errorApi = useApi(errorApiRef);

  // recursively resolve all filtered items via catalogClient
  const {
    error,
    loading,
    value: navigation,
  } = useAsync(async () => {
    const navResolver = new NavResolver(
      featureFlagApi,
      getEntitiesByFilter,
      errorApi,
      classes,
    );
    return (
      await Promise.all(navGroups.map(navResolver.resolveNavItemOrFilter))
    )
      .flat()
      .filter(notEmpty);
  }, [navGroups, getEntitiesByFilter, featureFlagApi, errorApi]);

  return { error, loading, navigation };
};
